// src/pages/CubbyManager.jsx
// ---------------------------------------------------------------------------
// Cubby Manager — for the team tracking student phones.
//
//   Tab 1  Phone Report    : pick a date, see who did not turn in a phone
//                            (by Day/Boarding cohort and grade), print a PDF.
//   Tab 2  Student Roster  : edit /students records — every field except sisId.
//
// Access: SuperAdmin grant of toolId "cubby_manager" AND the Dean role
// (superadmins always pass). Firestore rules enforce the same on writes.
//
// Data:
//   /phoneCheckIn/{YYYY-MM-DD}/records/*   studentId, status ("missing" = fail,
//                                          "absent" = absent per attendance, not a fail)
//   /students/{sisId}                      roster, keyed by sisId
//
// "Check attendance" calls the reconcilePhoneAbsences Cloud Function
// (functions/phoneAbsences.js), which cross-checks /attendance/{date} and
// flips fails for absent students to "absent". The same job runs nightly.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  collection, doc, getDocs, updateDoc, serverTimestamp, deleteField,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  buildPhoneReport, buildPhoneReportPdf, pdfFileName,
  todayAmman, longDate, shiftSchoolDay,
  studentName, sortName, cohortOf, gradeLabel, gradeNum, advisorOf, cubbyOf,
  isActiveStudent, compareCubby, COHORT_ORDER,
} from '../utils/cubbyManagerReport';

export const TOOL_ID = 'cubby_manager'; // byte-match: roles.js, App.js route, firestore.rules

/* ------------------------------------------------------------------------- */
/* Theme — King's branding                                                   */
/* ------------------------------------------------------------------------- */

const C = {
  navy: '#233C5F',
  red: '#E83530',
  gold: '#A29164',
  wash: '#F4F6F9', // light wash for zebra rows / tiles
  washLine: '#DDE3EC',
  ink: '#282C34',
  muted: '#6E7480',
  line: '#E3E6EB',
  white: '#FFFFFF',
  okBg: '#E9F5EC',
  ok: '#2E7D4F',
  warnBg: '#FDF1E3',
  warn: '#B7791F',
  errBg: '#FDECEC',
};

const font = 'inherit';

const S = {
  page: { padding: '1.5rem', maxWidth: 1180, margin: '0 auto', color: C.ink, fontFamily: font },
  banner: {
    position: 'relative', overflow: 'hidden', borderRadius: 12, background: C.navy,
    color: C.white, padding: '1.3rem 1.6rem', marginBottom: '1rem',
  },
  bannerRule: { position: 'absolute', left: 0, right: 0, bottom: 0, height: 4, background: C.gold },
  h1: { margin: 0, fontSize: '1.6rem', letterSpacing: 0.2 },
  sub: { margin: '0.35rem 0 0', color: '#E0D6B8', fontSize: '0.95rem' },
  tabs: { display: 'flex', gap: 6, borderBottom: `2px solid ${C.washLine}`, marginBottom: '1rem' },
  tab: (on) => ({
    border: 'none', background: on ? C.wash : 'transparent', color: on ? C.navy : C.muted,
    fontWeight: on ? 700 : 500, padding: '0.6rem 1rem', cursor: 'pointer', fontSize: '0.95rem',
    borderRadius: '8px 8px 0 0', borderBottom: on ? `2px solid ${C.gold}` : '2px solid transparent',
    marginBottom: -2, fontFamily: font,
  }),
  card: {
    background: C.white, border: `1px solid ${C.line}`, borderRadius: 10, padding: '1rem 1.1rem',
    boxShadow: '0 1px 2px rgba(35,60,95,0.06)',
  },
  row: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
  label: { fontSize: '0.78rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4 },
  input: {
    padding: '0.5rem 0.65rem', border: `1px solid ${C.line}`, borderRadius: 8, fontSize: '0.95rem',
    color: C.ink, background: C.white, fontFamily: font, minWidth: 0,
  },
  btn: (variant = 'primary', disabled = false) => {
    const base = {
      padding: '0.55rem 0.95rem', borderRadius: 8, fontWeight: 600, fontSize: '0.92rem', cursor: disabled ? 'not-allowed' : 'pointer',
      border: '1px solid transparent', opacity: disabled ? 0.55 : 1, fontFamily: font, whiteSpace: 'nowrap',
      display: 'inline-flex', alignItems: 'center', gap: 6,
    };
    if (variant === 'primary') return { ...base, background: C.navy, color: C.white };
    if (variant === 'gold') return { ...base, background: C.gold, color: C.white };
    if (variant === 'danger') return { ...base, background: C.white, color: C.red, borderColor: C.red };
    return { ...base, background: C.white, color: C.navy, borderColor: C.line };
  },
  tile: (accent) => ({
    flex: '1 1 160px', background: C.wash, border: `1px solid ${C.washLine}`, borderLeft: `5px solid ${accent}`,
    borderRadius: 10, padding: '0.8rem 1rem',
  }),
  tileNum: (accent) => ({ fontSize: '2rem', fontWeight: 800, color: accent, lineHeight: 1.1 }),
  tileLbl: { fontWeight: 700, fontSize: '0.9rem', marginTop: 2 },
  tileNote: { color: C.muted, fontSize: '0.8rem', marginTop: 2 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.92rem' },
  th: {
    textAlign: 'left', background: C.navy, color: C.white, padding: '0.55rem 0.7rem', fontSize: '0.8rem',
    fontWeight: 700, letterSpacing: 0.3, position: 'sticky', top: 0,
  },
  td: { padding: '0.5rem 0.7rem', borderBottom: `1px solid ${C.line}`, verticalAlign: 'middle' },
  pill: (bg, fg) => ({
    display: 'inline-block', padding: '0.12rem 0.55rem', borderRadius: 999, background: bg, color: fg,
    fontSize: '0.78rem', fontWeight: 700,
  }),
  notice: (kind) => ({
    padding: '0.7rem 0.9rem', borderRadius: 10, margin: '0.75rem 0', fontSize: '0.92rem',
    background: kind === 'error' ? C.errBg : kind === 'ok' ? C.okBg : C.warnBg,
    color: kind === 'error' ? C.red : kind === 'ok' ? C.ok : C.warn,
    border: `1px solid ${kind === 'error' ? '#F5B5B3' : kind === 'ok' ? '#B9DFC5' : '#F1D6A8'}`,
  }),
  sectionH: { display: 'flex', alignItems: 'center', gap: 8, margin: '1.1rem 0 0.5rem', color: C.navy, fontSize: '1.05rem', fontWeight: 700 },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(35,60,95,0.45)', display: 'flex', alignItems: 'flex-start',
    justifyContent: 'center', padding: '4vh 1rem', zIndex: 1000, overflowY: 'auto',
  },
  modal: { background: C.white, borderRadius: 12, width: 'min(760px, 100%)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' },
};

/* Gold rule used as a section marker. */
function Mark({ color = C.gold }) {
  return <span aria-hidden="true" style={{ display: 'inline-block', width: 4, height: 16, background: color, borderRadius: 2 }} />;
}

/* ------------------------------------------------------------------------- */
/* Page                                                                      */
/* ------------------------------------------------------------------------- */

export default function CubbyManager() {
  const { user, isSuperAdmin, isDean, allowedTools } = useAuth();
  const [tab, setTab] = useState('report');

  // Roster is shared by both tabs; loaded once, refreshed on demand.
  const [students, setStudents] = useState([]); // [{ id, ...data }]
  const [rosterLoading, setRosterLoading] = useState(true);
  const [rosterError, setRosterError] = useState(null);

  const loadRoster = useCallback(async () => {
    setRosterLoading(true);
    setRosterError(null);
    try {
      const snap = await getDocs(collection(db, 'students'));
      setStudents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setRosterError(`Could not load the student roster. ${err.message}`);
    } finally {
      setRosterLoading(false);
    }
  }, []);

  useEffect(() => { loadRoster(); }, [loadRoster]);

  const studentsById = useMemo(() => {
    const m = new Map();
    students.forEach((s) => {
      m.set(String(s.id), s);
      if (s.sisId) m.set(String(s.sisId), s);
    });
    return m;
  }, [students]);

  const hasGrant = isSuperAdmin || (Array.isArray(allowedTools) && allowedTools.includes(TOOL_ID));
  const allowed = isSuperAdmin || (isDean && hasGrant);

  if (!allowed) {
    return (
      <div style={S.page}>
        <Banner />
        <div style={S.notice('error')}>
          Cubby Manager must be granted by a SuperAdmin{!isDean && ' and requires the Dean role'}.
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <Banner />

      <div style={S.tabs} role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'report'} style={S.tab(tab === 'report')} onClick={() => setTab('report')}>
          Phone Report
        </button>
        <button type="button" role="tab" aria-selected={tab === 'roster'} style={S.tab(tab === 'roster')} onClick={() => setTab('roster')}>
          Student Roster
        </button>
      </div>

      {rosterError && <div style={S.notice('error')}>{rosterError}</div>}

      {tab === 'report' ? (
        <ReportTab studentsById={studentsById} rosterLoading={rosterLoading} userEmail={user?.email} />
      ) : (
        <RosterTab
          students={students}
          loading={rosterLoading}
          onReload={loadRoster}
          onLocalUpdate={(id, changes, removed) => setStudents((prev) => prev.map((s) => {
            if (s.id !== id) return s;
            const next = { ...s, ...changes };
            (removed || []).forEach((k) => delete next[k]);
            return next;
          }))}
          userEmail={user?.email}
          isSuperAdmin={isSuperAdmin}
        />
      )}
    </div>
  );
}

function Banner() {
  return (
    <div style={S.banner}>
      <h1 style={S.h1}>Cubby Manager</h1>
      <p style={S.sub}>Phone turn-in reports and student cubby assignments.</p>
      <div style={S.bannerRule} />
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Tab 1 — Phone Report                                                      */
/* ------------------------------------------------------------------------- */

function ReportTab({ studentsById, rosterLoading, userEmail }) {
  const [date, setDate] = useState(todayAmman());
  const [records, setRecords] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [marking, setMarking] = useState(null); // recordId being written
  const [showAbsent, setShowAbsent] = useState(false);

  const load = useCallback(async (d) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return;
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(collection(db, 'phoneCheckIn', d, 'records'));
      setRecords(snap.docs.map((x) => ({ _id: x.id, ...x.data() })));
    } catch (err) {
      setRecords([]);
      setError(`Could not load phone records for ${d}. ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { setInfo(null); load(date); }, [date, load]);

  // Cross-check this date against attendance (Cloud Function, Admin SDK).
  async function checkAttendance() {
    setChecking(true);
    setError(null);
    setInfo(null);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'reconcilePhoneAbsences');
      const { data } = await fn({ date });
      if (!data.attendanceFound) {
        setInfo({ kind: 'warn', text: `No attendance records exist yet for ${longDate(date)} — attendance usually lands around 11 PM. Nothing changed.` });
      } else {
        const parts = [];
        if (data.markedAbsent) parts.push(`${data.markedAbsent} marked absent`);
        if (data.restored) parts.push(`${data.restored} restored (now present in attendance)`);
        if (data.alreadyAbsent) parts.push(`${data.alreadyAbsent} already absent`);
        setInfo({
          kind: 'ok',
          text: `Checked ${data.attendanceRecords} attendance records against ${data.phoneRecords} phone records: ${parts.length ? parts.join(', ') : 'no changes needed'}.`,
        });
      }
      await load(date);
    } catch (err) {
      setError(`Attendance check failed. ${err.message}`);
    } finally {
      setChecking(false);
    }
  }

  // Manual override for a single record. A manual mark is never auto-restored
  // by the nightly job (it checks absentReconciledBy for the "system:" prefix).
  async function setAbsent(row, makeAbsent) {
    if (!row.recordId) return;
    setMarking(row.recordId);
    setError(null);
    try {
      const ref = doc(db, 'phoneCheckIn', date, 'records', row.recordId);
      if (makeAbsent) {
        await updateDoc(ref, {
          status: 'absent',
          priorStatus: row.status,
          absentReconciledAt: serverTimestamp(),
          absentReconciledBy: userEmail || 'cubby-manager',
        });
      } else {
        await updateDoc(ref, {
          status: row.priorStatus || 'missing',
          priorStatus: deleteField(),
          absentBlocks: deleteField(),
          absentOfBlocks: deleteField(),
          absenceStatuses: deleteField(),
          absentReconciledAt: deleteField(),
          absentReconciledBy: deleteField(),
          absentRestoredAt: serverTimestamp(),
          absentRestoredBy: userEmail || 'cubby-manager',
        });
      }
      setRecords((prev) => (prev || []).map((r) => {
        if (r._id !== row.recordId) return r;
        return makeAbsent
          ? { ...r, status: 'absent', priorStatus: row.status, absentReconciledBy: userEmail || 'cubby-manager' }
          : { ...r, status: row.priorStatus || 'missing', priorStatus: undefined, absentReconciledBy: undefined, absentBlocks: undefined, absentOfBlocks: undefined, absenceStatuses: undefined };
      }));
    } catch (err) {
      setError(`Could not update ${row.name}. ${err.message}`);
    } finally {
      setMarking(null);
    }
  }

  const report = useMemo(
    () => (records ? buildPhoneReport(records, studentsById) : null),
    [records, studentsById]
  );

  function downloadPdf() {
    if (!report) return;
    setPdfBusy(true);
    try {
      const pdf = buildPhoneReportPdf({ date, report, generatedBy: userEmail });
      pdf.save(pdfFileName(date));
    } catch (err) {
      setError(`Could not build the PDF. ${err.message}`);
    } finally {
      setPdfBusy(false);
    }
  }

  const s = report?.summary;
  const unresolved = report ? report.rows.filter((r) => !r.resolved).length : 0;

  return (
    <div>
      <div style={{ ...S.card, ...S.row, justifyContent: 'space-between' }}>
        <div style={S.row}>
          <span style={S.label}>Report date</span>
          <button type="button" style={S.btn('ghost')} onClick={() => setDate(shiftSchoolDay(date, -1))} title="Previous school day">‹</button>
          <input
            type="date"
            value={date}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            style={{ ...S.input, fontWeight: 600 }}
            aria-label="Report date"
          />
          <button type="button" style={S.btn('ghost')} onClick={() => setDate(shiftSchoolDay(date, 1))} title="Next school day">›</button>
          <button type="button" style={S.btn('ghost')} onClick={() => setDate(todayAmman())}>Today</button>
          <span style={{ color: C.muted, fontSize: '0.9rem' }}>{longDate(date)}</span>
        </div>
        <div style={S.row}>
          <button type="button" style={S.btn('ghost')} onClick={() => load(date)} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            type="button"
            style={S.btn('ghost', checking || loading)}
            disabled={checking || loading}
            onClick={checkAttendance}
            title="Cross-check this date against attendance and mark absent students"
          >
            {checking ? 'Checking…' : 'Check attendance'}
          </button>
          <button
            type="button"
            style={S.btn('primary', !report || loading || rosterLoading || pdfBusy)}
            disabled={!report || loading || rosterLoading || pdfBusy}
            onClick={downloadPdf}
          >
            {pdfBusy ? 'Building…' : 'Print PDF'}
          </button>
        </div>
      </div>

      {error && <div style={S.notice('error')}>{error}</div>}
      {info && <div style={S.notice(info.kind)}>{info.text}</div>}
      {rosterLoading && <div style={S.notice('warn')}>Loading the student roster so names, cohorts and cubbies resolve…</div>}

      {loading && !report && <p style={{ color: C.muted }}>Loading phone records…</p>}

      {report && (
        <>
          <div style={{ ...S.row, marginTop: '1rem', alignItems: 'stretch' }}>
            <div style={S.tile(C.red)}>
              <div style={S.tileNum(C.red)}>{s.failed}</div>
              <div style={S.tileLbl}>Phones not turned in</div>
              <div style={S.tileNote}>{s.failedPct}% of {s.expected} students expected</div>
            </div>
            <div style={S.tile(C.navy)}>
              <div style={S.tileNum(C.navy)}>{s.byCohort.Day}</div>
              <div style={S.tileLbl}>Day students</div>
            </div>
            <div style={S.tile(C.navy)}>
              <div style={S.tileNum(C.navy)}>{s.byCohort.Boarding}</div>
              <div style={S.tileLbl}>Boarding students</div>
            </div>
            {s.byCohort.Unknown > 0 && (
              <div style={S.tile(C.gold)}>
                <div style={S.tileNum(C.gold)}>{s.byCohort.Unknown}</div>
                <div style={S.tileLbl}>Cohort unknown</div>
                <div style={S.tileNote}>Set Day/Boarding in the Student Roster</div>
              </div>
            )}
            <div style={S.tile(C.muted)}>
              <div style={S.tileNum(C.muted)}>{s.absent}</div>
              <div style={S.tileLbl}>Absent — not counted</div>
              <div style={S.tileNote}>{s.absent ? 'Per attendance records' : 'Run "Check attendance" after the day ends'}</div>
            </div>
          </div>

          {unresolved > 0 && (
            <div style={S.notice('warn')}>
              {unresolved} record{unresolved === 1 ? '' : 's'} reference a student ID that is not in the roster —
              shown with the raw ID and any details the kiosk stored.
            </div>
          )}

          {s.totalRecords === 0 ? (
            <div style={{ ...S.card, marginTop: '1rem', color: C.muted }}>
              No phone check-in records for this date — a weekend, a holiday, or the kiosk has not run yet.
            </div>
          ) : report.rows.length === 0 ? (
            <div style={{ ...S.card, marginTop: '1rem', background: C.okBg, color: C.ok, borderColor: '#B9DFC5' }}>
              Every student who was at school turned in their phone.
            </div>
          ) : (
            <>
              <div style={S.sectionH}><Mark /> By grade and cohort</div>
              <div style={{ ...S.card, padding: 0, overflowX: 'auto' }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Grade</th>
                      <th style={{ ...S.th, textAlign: 'center' }}>Day</th>
                      <th style={{ ...S.th, textAlign: 'center' }}>Boarding</th>
                      {s.byCohort.Unknown > 0 && <th style={{ ...S.th, textAlign: 'center' }}>Unknown</th>}
                      <th style={{ ...S.th, textAlign: 'center' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.byGrade.map((g, i) => (
                      <tr key={g.label} style={{ background: i % 2 ? C.wash : C.white }}>
                        <td style={{ ...S.td, fontWeight: 700 }}>{g.label}</td>
                        <td style={{ ...S.td, textAlign: 'center' }}>{g.Day}</td>
                        <td style={{ ...S.td, textAlign: 'center' }}>{g.Boarding}</td>
                        {s.byCohort.Unknown > 0 && <td style={{ ...S.td, textAlign: 'center' }}>{g.Unknown}</td>}
                        <td style={{ ...S.td, textAlign: 'center', fontWeight: 700 }}>{g.total}</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#E2E7EF', fontWeight: 700, color: C.navy }}>
                      <td style={S.td}>All grades</td>
                      <td style={{ ...S.td, textAlign: 'center' }}>{s.byCohort.Day}</td>
                      <td style={{ ...S.td, textAlign: 'center' }}>{s.byCohort.Boarding}</td>
                      {s.byCohort.Unknown > 0 && <td style={{ ...S.td, textAlign: 'center' }}>{s.byCohort.Unknown}</td>}
                      <td style={{ ...S.td, textAlign: 'center' }}>{s.failed}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {COHORT_ORDER.map((cohort) => {
                const list = report.rows.filter((r) => r.cohort === cohort);
                if (!list.length) return null;
                return (
                  <div key={cohort}>
                    <div style={S.sectionH}>
                      <Mark /> {cohort === 'Unknown' ? 'Cohort unknown' : `${cohort} students`}
                      <span style={{ color: C.muted, fontWeight: 500, fontSize: '0.9rem' }}>({list.length})</span>
                    </div>
                    <div style={{ ...S.card, padding: 0, overflowX: 'auto' }}>
                      <table style={S.table}>
                        <thead>
                          <tr>
                            <th style={{ ...S.th, width: 40, textAlign: 'right' }}>#</th>
                            <th style={{ ...S.th, width: 80, textAlign: 'center' }}>Cubby</th>
                            <th style={S.th}>Student</th>
                            <th style={S.th}>Grade</th>
                            <th style={S.th}>Advisor</th>
                            <th style={S.th}>Status</th>
                            <th style={{ ...S.th, width: 110 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {list.map((r, i) => (
                            <tr key={r.id} style={{ background: i % 2 ? C.wash : C.white }}>
                              <td style={{ ...S.td, textAlign: 'right', color: C.muted }}>{i + 1}</td>
                              <td style={{ ...S.td, textAlign: 'center', fontWeight: 700 }}>{r.cubby || '—'}</td>
                              <td style={S.td}>
                                {r.name}
                                {!r.resolved && <span style={{ ...S.pill(C.warnBg, C.warn), marginLeft: 8 }}>not in roster</span>}
                              </td>
                              <td style={S.td}>{r.gradeLabel}</td>
                              <td style={S.td}>{r.advisor || '—'}</td>
                              <td style={S.td}><span style={S.pill(C.errBg, C.red)}>{r.status}</span></td>
                              <td style={{ ...S.td, textAlign: 'right' }}>
                                <button
                                  type="button"
                                  style={{ ...S.btn('ghost'), padding: '0.25rem 0.6rem', fontSize: '0.82rem' }}
                                  disabled={marking === r.recordId}
                                  onClick={() => setAbsent(r, true)}
                                  title="Student was absent — do not count this as a violation"
                                >
                                  {marking === r.recordId ? 'Saving…' : 'Mark absent'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {report.absentRows.length > 0 && (
            <div style={{ marginTop: '1.1rem' }}>
              <button
                type="button"
                onClick={() => setShowAbsent((v) => !v)}
                style={{ ...S.sectionH, background: 'none', border: 'none', cursor: 'pointer', padding: 0, margin: '0 0 0.5rem', color: C.muted, fontFamily: font }}
                aria-expanded={showAbsent}
              >
                <Mark color={C.washLine} /> Absent from school — not counted
                <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>({report.absentRows.length})</span>
                <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>{showAbsent ? 'Hide' : 'Show'}</span>
              </button>
              {showAbsent && (
                <div style={{ ...S.card, padding: 0, overflowX: 'auto', opacity: 0.9 }}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={{ ...S.th, background: C.muted, width: 80, textAlign: 'center' }}>Cubby</th>
                        <th style={{ ...S.th, background: C.muted }}>Student</th>
                        <th style={{ ...S.th, background: C.muted }}>Grade</th>
                        <th style={{ ...S.th, background: C.muted }}>Cohort</th>
                        <th style={{ ...S.th, background: C.muted }}>Attendance</th>
                        <th style={{ ...S.th, background: C.muted, width: 100 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {report.absentRows.map((r, i) => (
                        <tr key={r.id} style={{ background: i % 2 ? C.wash : C.white }}>
                          <td style={{ ...S.td, textAlign: 'center', fontWeight: 700 }}>{r.cubby || '—'}</td>
                          <td style={S.td}>{r.name}</td>
                          <td style={S.td}>{r.gradeLabel}</td>
                          <td style={S.td}>{r.cohort}</td>
                          <td style={{ ...S.td, color: C.muted, fontSize: '0.88rem' }}>
                            {r.manualMark
                              ? `Marked absent by ${r.markedBy}`
                              : r.absentOfBlocks
                                ? `${r.absentBlocks}/${r.absentOfBlocks} blocks absent${r.absenceStatuses.length ? ` · ${r.absenceStatuses.join(', ')}` : ''}`
                                : 'Absent per attendance'}
                          </td>
                          <td style={{ ...S.td, textAlign: 'right' }}>
                            <button
                              type="button"
                              style={{ ...S.btn('ghost'), padding: '0.25rem 0.6rem', fontSize: '0.82rem' }}
                              disabled={marking === r.recordId}
                              onClick={() => setAbsent(r, false)}
                              title="Put this back as a not-turned-in violation"
                            >
                              {marking === r.recordId ? 'Saving…' : 'Restore'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {s.totalRecords > 0 && (
            <p style={{ color: C.muted, fontSize: '0.82rem', marginTop: '1rem' }}>
              Status breakdown for {date}:{' '}
              {Object.entries(s.statusCounts).map(([k, v]) => `${k} ${v}`).join(' · ')}.
              Only <code>missing</code> / <code>notTurnedIn</code> count as a fail; <code>absent</code> is excluded.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Tab 2 — Student Roster                                                    */
/* ------------------------------------------------------------------------- */

const LOCKED_FIELDS = ['sisId'];
// `cohort` (older tools: MOPs, Check-In, Absences) and `residentStatus` (roster
// sync) both hold Day/Boarding. Every write here sets both so they never drift.
const RESIDENCY_FIELDS = ['cohort', 'residentStatus'];
const RESIDENCY_OPTIONS = ['Day', 'Boarding'];
// Dorm assignment for boarders. MOPs reads this via `houseField` in /config/mops.
export const HOUSE_FIELD = 'house';
const PINNED_FIELDS = [
  'firstName', 'lastName', 'name', 'fullName', 'grade', 'cohort', 'residentStatus', HOUSE_FIELD, 'cubby',
  'advisorName', 'advisorId', 'email', 'gender', 'active', 'status',
];
const READONLY_META = ['updatedAt', 'updatedBy', 'createdAt', 'lastSyncedAt', 'ingestedAt'];

const houseOf = (s) => (s && s[HOUSE_FIELD] != null ? String(s[HOUSE_FIELD]).trim() : '');

function RosterTab({ students, loading, onReload, onLocalUpdate, userEmail, isSuperAdmin }) {
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [sortKey, setSortKey] = useState('name'); // name | cubby | grade
  const [editing, setEditing] = useState(null); // student object (full editor)
  const [housing, setHousing] = useState(null); // student object (housing panel)
  const [notice, setNotice] = useState(null);
  const [cubbyDraft, setCubbyDraft] = useState({}); // id -> value while editing inline
  const [saving, setSaving] = useState(null); // id being saved inline

  // Duplicate cubby detection among active students.
  const cubbyOwners = useMemo(() => {
    const m = new Map();
    students.filter(isActiveStudent).forEach((s) => {
      const c = cubbyOf(s);
      if (!c) return;
      if (!m.has(c)) m.set(c, []);
      m.get(c).push(s);
    });
    return m;
  }, [students]);
  const dupeCount = Array.from(cubbyOwners.values()).filter((v) => v.length > 1).length;

  // Every dorm name in use (active students), for the dropdown / datalist.
  const knownHouses = useMemo(() => {
    const set = new Set();
    students.filter(isActiveStudent).forEach((s) => { const h = houseOf(s); if (h) set.add(h); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [students]);

  // Boarders with no dorm — worth surfacing since that's what this panel fixes.
  const boardersNoDorm = useMemo(
    () => students.filter((s) => isActiveStudent(s) && cohortOf(s) === 'Boarding' && !houseOf(s)).length,
    [students]
  );

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = students.filter((s) => showInactive || isActiveStudent(s));
    if (needle) {
      list = list.filter((s) => {
        const hay = [
          studentName(s), s.id, s.sisId, cubbyOf(s), advisorOf(s), gradeLabel(s), cohortOf(s), houseOf(s), s.email,
        ].map((v) => String(v || '').toLowerCase());
        return hay.some((h) => h.includes(needle));
      });
    }
    const byName = (a, b) => sortName(a).localeCompare(sortName(b));
    if (sortKey === 'cubby') {
      list.sort((a, b) => {
        const ca = cubbyOf(a); const cb = cubbyOf(b);
        if (!ca && cb) return 1;
        if (ca && !cb) return -1;
        return compareCubby(ca, cb) || byName(a, b);
      });
    } else if (sortKey === 'dorm') {
      list.sort((a, b) => {
        const ha = houseOf(a); const hb = houseOf(b);
        if (!ha && hb) return 1;
        if (ha && !hb) return -1;
        return ha.localeCompare(hb, undefined, { numeric: true }) || byName(a, b);
      });
    } else if (sortKey === 'grade') {
      list.sort((a, b) => ((gradeNum(a) ?? 99) - (gradeNum(b) ?? 99)) || byName(a, b));
    } else {
      list.sort(byName);
    }
    return list;
  }, [students, q, showInactive, sortKey]);

  async function saveFields(student, changes, removed = []) {
    // Belt and braces: the rules block sisId too, but never even try.
    LOCKED_FIELDS.forEach((k) => { delete changes[k]; });
    // Keep cohort and residentStatus identical: if either is being written,
    // write the same value to both.
    const residency = RESIDENCY_FIELDS.map((k) => changes[k]).find((v) => v !== undefined);
    if (residency !== undefined) RESIDENCY_FIELDS.forEach((k) => { changes[k] = residency; });
    const payload = { ...changes };
    removed.forEach((k) => { if (!LOCKED_FIELDS.includes(k)) payload[k] = deleteField(); });
    if (Object.keys(payload).length === 0) return;
    payload.updatedAt = serverTimestamp();
    payload.updatedBy = userEmail || 'cubby-manager';
    await updateDoc(doc(db, 'students', student.id), payload);
    onLocalUpdate(student.id, { ...changes, updatedBy: payload.updatedBy }, removed);
  }

  async function commitCubby(s) {
    const val = (cubbyDraft[s.id] ?? '').trim();
    if (val === cubbyOf(s)) {
      setCubbyDraft((d) => { const n = { ...d }; delete n[s.id]; return n; });
      return;
    }
    setSaving(s.id);
    setNotice(null);
    try {
      await saveFields(s, { cubby: val });
      setNotice({ kind: 'ok', text: `Cubby for ${studentName(s)} set to ${val || '(none)'}.` });
    } catch (err) {
      setNotice({ kind: 'error', text: `Could not save cubby for ${studentName(s)}. ${err.message}` });
    } finally {
      setSaving(null);
      setCubbyDraft((d) => { const n = { ...d }; delete n[s.id]; return n; });
    }
  }

  return (
    <div>
      <div style={{ ...S.card, ...S.row, justifyContent: 'space-between' }}>
        <div style={{ ...S.row, flex: 1 }}>
          <input
            type="search"
            placeholder="Search name, ID, cubby, advisor, grade…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ ...S.input, flex: '1 1 260px' }}
            aria-label="Search students"
          />
          <label style={{ ...S.row, gap: 6, fontSize: '0.9rem', color: C.muted }}>
            <span style={S.label}>Sort</span>
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} style={S.input}>
              <option value="name">Last name</option>
              <option value="cubby">Cubby</option>
              <option value="dorm">Dorm</option>
              <option value="grade">Grade</option>
            </select>
          </label>
          <label style={{ ...S.row, gap: 6, fontSize: '0.9rem', color: C.muted, cursor: 'pointer' }}>
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            Show inactive
          </label>
        </div>
        <div style={S.row}>
          <span style={{ color: C.muted, fontSize: '0.9rem' }}>
            {visible.length} of {students.filter((s) => showInactive || isActiveStudent(s)).length}
          </span>
          <button type="button" style={S.btn('ghost')} onClick={onReload} disabled={loading}>
            {loading ? 'Loading…' : 'Reload'}
          </button>
        </div>
      </div>

      {dupeCount > 0 && (
        <div style={S.notice('warn')}>
          {dupeCount} cubby number{dupeCount === 1 ? ' is' : 's are'} assigned to more than one active student —
          flagged in the Cubby column. Sort by cubby to see them together.
        </div>
      )}
      {boardersNoDorm > 0 && (
        <div style={S.notice('warn')}>
          {boardersNoDorm} boarding student{boardersNoDorm === 1 ? ' has' : 's have'} no dorm assigned.
          Search "boarding", sort by dorm, and use <strong>Housing</strong> on each row to assign one.
        </div>
      )}
      {notice && <div style={S.notice(notice.kind)}>{notice.text}</div>}

      <div style={{ ...S.card, padding: 0, marginTop: '1rem', overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto' }}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: 110 }}>Cubby</th>
              <th style={S.th}>Student</th>
              <th style={S.th}>SIS ID</th>
              <th style={S.th}>Grade</th>
              <th style={S.th}>Residency</th>
              <th style={S.th}>Dorm</th>
              <th style={S.th}>Advisor</th>
              <th style={{ ...S.th, width: 170 }} />
            </tr>
          </thead>
          <tbody>
            {loading && students.length === 0 && (
              <tr><td style={S.td} colSpan={8}>Loading roster…</td></tr>
            )}
            {!loading && visible.length === 0 && (
              <tr><td style={{ ...S.td, color: C.muted }} colSpan={8}>No students match.</td></tr>
            )}
            {visible.map((s, i) => {
              const cubby = cubbyOf(s);
              const dupe = cubby && (cubbyOwners.get(cubby) || []).length > 1 && isActiveStudent(s);
              const inactive = !isActiveStudent(s);
              const draft = cubbyDraft[s.id];
              const cohort = cohortOf(s);
              return (
                <tr key={s.id} style={{ background: i % 2 ? C.wash : C.white, opacity: inactive ? 0.6 : 1 }}>
                  <td style={S.td}>
                    <div style={{ ...S.row, gap: 4, flexWrap: 'nowrap' }}>
                      <input
                        value={draft ?? cubby}
                        onChange={(e) => setCubbyDraft((d) => ({ ...d, [s.id]: e.target.value }))}
                        onBlur={() => draft !== undefined && commitCubby(s)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.currentTarget.blur();
                          if (e.key === 'Escape') setCubbyDraft((d) => { const n = { ...d }; delete n[s.id]; return n; });
                        }}
                        disabled={saving === s.id}
                        style={{
                          ...S.input, width: 64, textAlign: 'center', fontWeight: 700, padding: '0.3rem 0.4rem',
                          borderColor: dupe ? C.red : draft !== undefined ? C.gold : C.line,
                        }}
                        aria-label={`Cubby for ${studentName(s)}`}
                        title={dupe ? 'This cubby is assigned to more than one active student' : 'Edit cubby — Enter to save, Esc to cancel'}
                      />
                      {dupe && <span title="Duplicate cubby" aria-label="Duplicate cubby" style={{ color: C.red, fontWeight: 700 }}>!</span>}
                    </div>
                  </td>
                  <td style={{ ...S.td, fontWeight: 600 }}>
                    {studentName(s) || <span style={{ color: C.muted }}>(no name)</span>}
                    {inactive && <span style={{ ...S.pill('#EEE', C.muted), marginLeft: 8 }}>inactive</span>}
                  </td>
                  <td style={{ ...S.td, fontFamily: 'ui-monospace, monospace', color: C.muted }}>{s.sisId || s.id}</td>
                  <td style={S.td}>{gradeLabel(s)}</td>
                  <td style={S.td}>
                    {cohort === 'Unknown'
                      ? <span style={S.pill(C.warnBg, C.warn)}>unknown</span>
                      : <span style={S.pill(cohort === 'Boarding' ? '#E8EDF5' : C.wash, C.navy)}>{cohort}</span>}
                  </td>
                  <td style={S.td}>
                    {houseOf(s)
                      ? houseOf(s)
                      : cohort === 'Boarding'
                        ? <span style={S.pill(C.warnBg, C.warn)}>no dorm</span>
                        : <span style={{ color: C.muted }}>—</span>}
                  </td>
                  <td style={S.td}>{advisorOf(s) || '—'}</td>
                  <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button type="button" style={{ ...S.btn('primary'), padding: '0.3rem 0.7rem', marginRight: 6 }} onClick={() => setHousing(s)}>
                      Housing
                    </button>
                    <button type="button" style={{ ...S.btn('ghost'), padding: '0.3rem 0.7rem' }} onClick={() => setEditing(s)}>
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {housing && (
        <HousingModal
          student={housing}
          knownHouses={knownHouses}
          cubbyOwners={cubbyOwners}
          onClose={() => setHousing(null)}
          onSave={async (changes, removed) => {
            await saveFields(housing, changes, removed);
            setNotice({ kind: 'ok', text: `${studentName(housing) || housing.id} updated.` });
            setHousing(null);
          }}
        />
      )}

      {editing && (
        <EditStudentModal
          student={editing}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setEditing(null)}
          onSave={async (changes, removed) => {
            await saveFields(editing, changes, removed);
            setNotice({ kind: 'ok', text: `${studentName({ ...editing, ...changes }) || editing.id} updated.` });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Housing modal — residency, dorm, cubby in one place                       */
/* ------------------------------------------------------------------------- */

function HousingModal({ student, knownHouses, cubbyOwners, onClose, onSave }) {
  const startResidency = cohortOf(student) === 'Unknown' ? '' : cohortOf(student);
  const [residency, setResidency] = useState(startResidency);
  const [house, setHouse] = useState(houseOf(student));
  const [cubby, setCubby] = useState(cubbyOf(student));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isBoarding = residency === 'Boarding';
  const cubbyTrim = cubby.trim();
  const otherOwners = (cubbyOwners.get(cubbyTrim) || []).filter((s) => s.id !== student.id);

  const residencyChanged = residency !== startResidency
    || RESIDENCY_FIELDS.some((k) => (student[k] ?? '') !== residency); // also repairs a cohort/residentStatus mismatch
  const houseNext = isBoarding ? house.trim() : '';
  const houseChanged = houseNext !== houseOf(student);
  const cubbyChanged = cubbyTrim !== cubbyOf(student);
  const dirty = residencyChanged || houseChanged || cubbyChanged;

  async function submit(e) {
    e.preventDefault();
    setErr(null);
    if (!residency) { setErr('Choose Day or Boarding.'); return; }
    if (isBoarding && !houseNext) { setErr('A boarding student needs a dorm. Pick one or type a new name.'); return; }
    const changes = {};
    const removed = [];
    if (residencyChanged) changes.cohort = residency; // saveFields mirrors to residentStatus
    if (houseChanged) {
      if (houseNext) changes[HOUSE_FIELD] = houseNext;
      else if (HOUSE_FIELD in student) removed.push(HOUSE_FIELD); // moved to Day: drop the dorm
    }
    if (cubbyChanged) changes.cubby = cubbyTrim;
    if (!Object.keys(changes).length && !removed.length) { onClose(); return; }
    setBusy(true);
    try {
      await onSave(changes, removed);
    } catch (ex) {
      setErr(`Save failed. ${ex.message}`);
      setBusy(false);
    }
  }

  const field = { ...S.input, width: '100%', boxSizing: 'border-box' };
  const mismatch = RESIDENCY_FIELDS.map((k) => student[k]).filter((v, i, a) => a.indexOf(v) === i).length > 1;

  return (
    <div style={S.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form style={{ ...S.modal, width: 'min(520px, 100%)' }} onSubmit={submit} role="dialog" aria-modal="true" aria-label="Housing and cubby">
        <div style={{ background: C.navy, color: C.white, padding: '0.9rem 1.2rem', position: 'relative' }}>
          <div style={{ fontSize: '0.78rem', color: '#E0D6B8', letterSpacing: 0.4, textTransform: 'uppercase' }}>Housing &amp; cubby</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 700, marginTop: 2 }}>{studentName(student) || student.id}</div>
          <div style={{ fontSize: '0.82rem', color: '#C9D2E0', marginTop: 2 }}>{gradeLabel(student)} · {student.sisId || student.id}</div>
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3, background: C.gold }} />
        </div>

        <div style={{ padding: '1rem 1.2rem', display: 'grid', gap: 14 }}>
          {mismatch && (
            <div style={{ ...S.notice('warn'), margin: 0 }}>
              This record's <code>cohort</code> and <code>residentStatus</code> disagree
              ({String(student.cohort ?? '—')} / {String(student.residentStatus ?? '—')}). Saving sets both to your choice.
            </div>
          )}

          <div>
            <div style={{ ...S.label, marginBottom: 6 }}>Residency</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {RESIDENCY_OPTIONS.map((opt) => {
                const on = residency === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setResidency(opt)}
                    aria-pressed={on}
                    style={{
                      ...S.btn(on ? 'primary' : 'ghost'), flex: 1, justifyContent: 'center',
                      borderColor: on ? C.navy : C.line,
                    }}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label style={{ ...S.label, display: 'block', marginBottom: 6 }} htmlFor="housing-dorm">
              Dorm {!isBoarding && <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>— day students have no dorm</span>}
            </label>
            <input
              id="housing-dorm"
              list="housing-dorm-options"
              value={isBoarding ? house : ''}
              onChange={(e) => setHouse(e.target.value)}
              disabled={!isBoarding}
              placeholder={isBoarding ? 'Choose a dorm or type a new one' : ''}
              style={{ ...field, background: isBoarding ? C.white : '#F3F4F6' }}
              autoComplete="off"
            />
            <datalist id="housing-dorm-options">
              {knownHouses.map((h) => <option key={h} value={h} />)}
            </datalist>
            {isBoarding && knownHouses.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                {knownHouses.map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setHouse(h)}
                    style={{ ...S.pill(house.trim() === h ? C.navy : C.wash, house.trim() === h ? C.white : C.navy), border: `1px solid ${C.washLine}`, cursor: 'pointer' }}
                  >
                    {h}
                  </button>
                ))}
              </div>
            )}
            {isBoarding && house.trim() && !knownHouses.includes(house.trim()) && (
              <div style={{ color: C.muted, fontSize: '0.82rem', marginTop: 6 }}>
                New dorm name — it will appear in this list once saved.
              </div>
            )}
            {!isBoarding && houseOf(student) && (
              <div style={{ color: C.warn, fontSize: '0.82rem', marginTop: 6 }}>
                Saving as Day removes the current dorm ({houseOf(student)}) from this record.
              </div>
            )}
          </div>

          <div>
            <label style={{ ...S.label, display: 'block', marginBottom: 6 }} htmlFor="housing-cubby">Cubby</label>
            <input
              id="housing-cubby"
              value={cubby}
              onChange={(e) => setCubby(e.target.value)}
              placeholder="e.g. G9-6"
              style={{ ...field, fontWeight: 700, borderColor: otherOwners.length ? C.red : C.line }}
              autoComplete="off"
            />
            {otherOwners.length > 0 && (
              <div style={{ color: C.red, fontSize: '0.82rem', marginTop: 6 }}>
                Already assigned to {otherOwners.map((o) => studentName(o) || o.id).join(', ')}. You can still save, but it will be flagged as a duplicate.
              </div>
            )}
          </div>

          {err && <div style={{ ...S.notice('error'), margin: 0 }}>{err}</div>}
        </div>

        <div style={{ ...S.row, justifyContent: 'space-between', padding: '0.85rem 1.2rem', borderTop: `1px solid ${C.line}`, background: C.wash }}>
          <span style={{ color: C.muted, fontSize: '0.82rem' }}>
            Writes <code>cohort</code>, <code>residentStatus</code>, <code>{HOUSE_FIELD}</code>, <code>cubby</code>.
          </span>
          <div style={S.row}>
            <button type="button" style={S.btn('ghost')} onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" style={S.btn('primary', busy || !dirty)} disabled={busy || !dirty}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Edit modal — every field except sisId                                     */
/* ------------------------------------------------------------------------- */

function kindOf(v) {
  if (v === null || v === undefined) return 'text';
  if (typeof v === 'boolean') return 'bool';
  if (typeof v === 'number') return 'number';
  if (typeof v === 'string') return 'text';
  if (v && typeof v.toDate === 'function') return 'timestamp';
  return 'json';
}

function toDraft(v) {
  const k = kindOf(v);
  if (k === 'bool') return v ? 'true' : 'false';
  if (k === 'number') return String(v);
  if (k === 'timestamp') return v.toDate().toLocaleString();
  if (k === 'json') return JSON.stringify(v, null, 2);
  return v ?? '';
}

function fromDraft(kind, raw) {
  if (kind === 'bool') return raw === 'true';
  if (kind === 'number') {
    if (raw.trim() === '') return null;
    const n = Number(raw);
    if (Number.isNaN(n)) throw new Error('must be a number');
    return n;
  }
  if (kind === 'json') return JSON.parse(raw);
  return raw;
}

function EditStudentModal({ student, isSuperAdmin, onClose, onSave }) {
  const { id, ...data } = student;

  const fieldNames = useMemo(() => {
    const keys = Object.keys(data);
    const pinned = PINNED_FIELDS.filter((k) => keys.includes(k));
    const rest = keys.filter((k) => !pinned.includes(k) && !LOCKED_FIELDS.includes(k) && !READONLY_META.includes(k)).sort();
    const meta = keys.filter((k) => READONLY_META.includes(k)).sort();
    // Always offer the common ones even if the doc lacks them yet.
    const missingPinned = ['firstName', 'lastName', 'grade', 'cohort', 'residentStatus', HOUSE_FIELD, 'cubby', 'advisorName'].filter((k) => !keys.includes(k));
    return { pinned: [...pinned, ...missingPinned], rest, meta };
  }, [data]);

  const initial = useMemo(() => {
    const d = {};
    [...fieldNames.pinned, ...fieldNames.rest].forEach((k) => {
      d[k] = { kind: kindOf(data[k]), value: toDraft(data[k]), existed: k in data };
    });
    return d;
  }, [data, fieldNames]);

  const [drafts, setDrafts] = useState(initial);
  const [removed, setRemoved] = useState([]);
  const [newKey, setNewKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function setVal(k, value) {
    setDrafts((d) => ({ ...d, [k]: { ...d[k], value } }));
  }

  function addField() {
    const k = newKey.trim();
    if (!k) return;
    if (LOCKED_FIELDS.includes(k)) { setErr(`${k} cannot be changed.`); return; }
    if (drafts[k]) { setErr(`${k} already exists.`); return; }
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(k)) { setErr('Field names: letters, numbers and underscores only.'); return; }
    setDrafts((d) => ({ ...d, [k]: { kind: 'text', value: '', existed: false } }));
    setNewKey('');
    setErr(null);
  }

  function removeField(k) {
    setDrafts((d) => { const n = { ...d }; delete n[k]; return n; });
    if (k in data) setRemoved((r) => [...r, k]);
  }

  async function submit(e) {
    e.preventDefault();
    setErr(null);
    const changes = {};
    try {
      Object.entries(drafts).forEach(([k, d]) => {
        if (LOCKED_FIELDS.includes(k)) return;
        let next;
        try {
          next = fromDraft(d.kind, d.value);
        } catch (ex) {
          throw new Error(`${k}: ${ex.message}`);
        }
        const before = data[k];
        const same = d.kind === 'json' ? JSON.stringify(before) === JSON.stringify(next) : (before ?? '') === (next ?? '');
        if (!same) changes[k] = next;
      });
    } catch (ex) {
      setErr(ex.message);
      return;
    }
    if (Object.keys(changes).length === 0 && removed.length === 0) { onClose(); return; }
    setBusy(true);
    try {
      await onSave(changes, removed);
    } catch (ex) {
      setErr(`Save failed. ${ex.message}`);
      setBusy(false);
    }
  }

  const renderField = (k) => {
    const d = drafts[k];
    if (!d) return null;
    const common = { ...S.input, width: '100%', boxSizing: 'border-box' };
    let control;
    if (d.kind === 'bool') {
      control = (
        <select value={d.value} onChange={(e) => setVal(k, e.target.value)} style={common}>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    } else if (d.kind === 'json') {
      control = (
        <textarea value={d.value} onChange={(e) => setVal(k, e.target.value)} rows={Math.min(8, d.value.split('\n').length + 1)} style={{ ...common, fontFamily: 'ui-monospace, monospace', fontSize: '0.85rem' }} />
      );
    } else if (d.kind === 'timestamp') {
      control = <input value={d.value} readOnly style={{ ...common, color: C.muted, background: '#FAFAFA' }} />;
    } else if (RESIDENCY_FIELDS.includes(k)) {
      const opts = RESIDENCY_OPTIONS;
      const custom = d.value && !opts.includes(d.value);
      control = (
        <select value={d.value} onChange={(e) => setVal(k, e.target.value)} style={common}>
          <option value="">(blank)</option>
          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
          {custom && <option value={d.value}>{d.value}</option>}
        </select>
      );
    } else {
      control = (
        <input
          type={d.kind === 'number' ? 'number' : 'text'}
          value={d.value}
          onChange={(e) => setVal(k, e.target.value)}
          style={common}
        />
      );
    }
    return (
      <div key={k} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 32px', gap: 8, alignItems: 'start', marginBottom: 8 }}>
        <label style={{ ...S.label, paddingTop: 10, wordBreak: 'break-all' }} htmlFor={`f-${k}`}>
          {k}{!d.existed && <span style={{ color: C.gold }}> · new</span>}
        </label>
        <div id={`f-${k}`}>{control}</div>
        {d.kind !== 'timestamp' && (
          <button
            type="button"
            onClick={() => removeField(k)}
            title={`Remove ${k} from this record`}
            style={{ ...S.btn('ghost'), padding: '0.35rem 0.5rem', color: C.muted, marginTop: 2 }}
          >
            ✕
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={S.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form style={S.modal} onSubmit={submit} role="dialog" aria-modal="true" aria-label="Edit student">
        <div style={{ background: C.navy, color: C.white, padding: '0.9rem 1.2rem', position: 'relative' }}>
          <div style={{ fontSize: '0.78rem', color: '#E0D6B8', letterSpacing: 0.4, textTransform: 'uppercase' }}>Student Roster · edit student</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 700, marginTop: 2 }}>{studentName(student) || id}</div>
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3, background: C.gold }} />
        </div>

        <div style={{ padding: '1rem 1.2rem', maxHeight: '65vh', overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 32px', gap: 8, alignItems: 'center', marginBottom: 12 }}>
            <span style={S.label}>sisId (locked)</span>
            <input value={data.sisId ?? id} readOnly style={{ ...S.input, width: '100%', boxSizing: 'border-box', background: '#F3F4F6', color: C.muted, fontFamily: 'ui-monospace, monospace' }} title="The SIS ID is the record key and cannot be changed" />
            <span />
          </div>

          {fieldNames.pinned.map(renderField)}

          {fieldNames.rest.length > 0 && (
            <>
              <div style={{ ...S.sectionH, fontSize: '0.9rem', margin: '1rem 0 0.5rem' }}><Mark /> Other fields</div>
              {fieldNames.rest.map(renderField)}
            </>
          )}

          {fieldNames.meta.length > 0 && (
            <>
              <div style={{ ...S.sectionH, fontSize: '0.9rem', margin: '1rem 0 0.5rem', color: C.muted }}><Mark color={C.washLine} /> Record metadata (read-only)</div>
              {fieldNames.meta.map((k) => (
                <div key={k} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 32px', gap: 8, marginBottom: 6, color: C.muted, fontSize: '0.88rem' }}>
                  <span style={S.label}>{k}</span>
                  <span>{toDraft(data[k])}</span>
                </div>
              ))}
            </>
          )}

          <div style={{ ...S.row, marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${C.washLine}` }}>
            <input
              placeholder="Add a field (e.g. house)"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addField(); } }}
              style={{ ...S.input, flex: 1 }}
            />
            <button type="button" style={S.btn('ghost')} onClick={addField}>Add field</button>
          </div>

          {removed.length > 0 && (
            <div style={S.notice('warn')}>Will remove from this record on save: {removed.join(', ')}</div>
          )}
          {err && <div style={S.notice('error')}>{err}</div>}
        </div>

        <div style={{ ...S.row, justifyContent: 'space-between', padding: '0.85rem 1.2rem', borderTop: `1px solid ${C.line}`, background: C.wash }}>
          <span style={{ color: C.muted, fontSize: '0.82rem' }}>
            Writes go straight to <code>/students/{id}</code>{isSuperAdmin ? ' (superadmin)' : ''}.
          </span>
          <div style={S.row}>
            <button type="button" style={S.btn('ghost')} onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" style={S.btn('primary', busy)} disabled={busy}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}